import React, { useEffect, useMemo, useState } from 'react';

// Styles & Assets
import styles from './PatientAssignPreconfigsDialog.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useTranslation } from 'react-i18next';
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// Components
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import CircleSelector from '../../UI/CircleSelector/CircleSelector';

// Interfaces
import { PatientPreconfig, Preconfiguration } from '../../../interfaces/Preconfigurations';
import usePatientStore from '../../../stores/patient-store';

interface Props {
    caseID: number;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    onAssigned: (patientPreconfig: PatientPreconfig) => void;
    refreshPatientSilently: () => Promise<void>;
}

const PatientAssignPreconfigsDialog: React.FC<Props> = ({
    caseID,
    setShow,
    onAssigned,
    refreshPatientSilently
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const { sendRequest } = useHttp();
    const { isLoading, error, sendRequest: getPreconfigs } = useHttp<Preconfiguration[]>();

    const [preconfigs, setPreconfigs] = useState<Preconfiguration[]>([]);
    const [selectedID, setSelectedID] = useState<Preconfiguration['id'] | null>(null);
    const [isAssigning, setIsAssigning] = useState(false);

    const updatePatient = usePatientStore(state => state.updatePatient);

    useEffect(() => {
        getPreconfigs(
            {
                url: getEnvironmentApiUrl() + 'preconfig/all',
                method: 'POST',
                body: { params: {}, filter: {} }
            },
            (data) => setPreconfigs(data)
        );
    }, [getPreconfigs]);

    const visiblePreconfigs = useMemo(() => {
        return preconfigs.filter(p => !p.is_patient_override);
    }, [preconfigs]);

    const handleClick = (id: Preconfiguration['id']) => () => {
        if (isAssigning) return;
        setSelectedID(id);
    };

    const handleConfirm = () => {
        if (isAssigning) return;

        if (selectedID == null) {
            setShow(false);
            return;
        }

        const selectedPreconfig = visiblePreconfigs.find(prec => prec.id === selectedID);

        if (!selectedPreconfig) {
            toast.error(t('error_assigning_patient'));
            return;
        }

        setIsAssigning(true);

        sendRequest(
            {
                url: getEnvironmentApiUrl() + 'preconfig/' + selectedID + '/assign_cases',
                method: 'POST',
                body: [caseID]
            },
            async () => {
                const optimisticPatientPreconfig: PatientPreconfig = {
                    preconfig_id: selectedID,
                    case_id: caseID,
                    start_date: new Date().toISOString(),
                    end_date: null,
                    preconfig: selectedPreconfig
                };

                updatePatient({
                    patient_preconfigs: [optimisticPatientPreconfig]
                });

                onAssigned(optimisticPatientPreconfig);

                try {
                    await refreshPatientSilently();
                } catch (e) {
                    console.error('[PRECONFIG] refreshPatientSilently failed', e);
                } finally {
                    setIsAssigning(false);
                }
            },
            () => {
                setIsAssigning(false);
                toast.error(t('error_assigning_patient'));
            }
        );
    };

    return (
        <div className='dialog'>
            <h1 className={`dialogTitle ${styles.title}`}>
                {t('assign_preconfig')}
            </h1>

            <ErrorSpinnerWrapper loading={isLoading || isAssigning} error={error}>
                <div className={styles.content}>
                    {visiblePreconfigs.map(prec => (
                        <CircleSelector
                            key={prec.id}
                            text={prec.name}
                            active={selectedID === prec.id}
                            handleClick={handleClick(prec.id)}
                        />
                    ))}
                </div>
            </ErrorSpinnerWrapper>

            <CancelConfirmButtons
                onCancel={() => {
                    if (isAssigning) return;
                    setShow(false);
                }}
                onConfirm={handleConfirm}
            />
        </div>
    );
};

export default PatientAssignPreconfigsDialog;